import React from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';

interface Step {
  icon: string;
  title: string;
  duration: string;
  description: string;
  deliverables: string[];
}

const steps: Step[] = [
  {
    icon: '🧭',
    title: 'Aanvraag via de configurator',
    duration: 'Direct',
    description:
      'Je stelt online je pakket samen: website, app of beide, een doorlooptijd en de gewenste uitbreidingen. Je krijgt direct een richtprijs en een overzicht van wat er bij die scope hoort.',
    deliverables: ['Richtprijs', 'Pakketselectie', 'Projectomschrijving'],
  },
  {
    icon: '☕',
    title: 'Kennismakingsgesprek',
    duration: '30 min',
    description:
      'Een korte videocall of afspraak op locatie, wat jij prettig vindt. We luisteren naar je idee, wensen en doelen, en denken mee waar dat helpt.',
    deliverables: ['Helder beeld van scope', 'Afstemming over deadline', 'Eerste advies'],
  },
  {
    icon: '🎨',
    title: 'Wireframes & design',
    duration: 'Ontwerpfase',
    description:
      'Na akkoord maken we eerst wireframes voor de designs, zodat we vroeg kunnen valideren. Daarna werken we het visueel concept uit in Figma met moodboard, kleurpalet, typografie en mockups van elke pagina of app-scherm. Je krijgt meerdere feedbackrondes om alles aan te scherpen.',
    deliverables: ['Sitemap', 'Wireframes', 'Hi-fi mockups', 'Klikbare prototype'],
  },
  {
    icon: '⚙️',
    title: 'Development & integraties',
    duration: 'Bouwfase',
    description:
      'Zodra het ontwerp staat, bouwen we het in code. Frontend, backend, koppelingen met betalingen, advertentienetwerken, authenticatie of push notificaties, afhankelijk van je gekozen uitbreidingen. Je krijgt tussentijds toegang tot een staging-omgeving.',
    deliverables: ['Staging-omgeving', 'Werkende integraties', 'Tussentijdse demo\'s'],
  },
  {
    icon: '🧪',
    title: 'Testen & QA',
    duration: 'QA-fase',
    description:
      'We testen op meerdere devices en browsers, checken laadtijden, toegankelijkheid en SEO-basis. Voor apps testen we op zowel iOS als Android en zorgen we dat alles voldoet aan de store-richtlijnen.',
    deliverables: ['Device/browser-check', 'Performance audit', 'SEO-basis (indien gekozen)'],
  },
  {
    icon: '🏁',
    title: 'Oplevering & lancering',
    duration: 'Lancering',
    description:
      'Domein en hosting worden gekoppeld, app builds ingediend bij de stores (indien van toepassing) en de site of app gaat live. Je krijgt een korte handover met alle toegangen en uitleg over het beheer.',
    deliverables: ['Live website / app', 'Handover-document', 'Toegangen'],
  },
  {
    icon: '🤝',
    title: 'Nazorg & doorontwikkeling',
    duration: '7 dagen + optioneel',
    description:
      'Na de oplevering heb je 7 dagen om eventuele gebreken te melden. Die lossen we binnen scope op. Daarna kun je kiezen voor doorlopend onderhoud, doorontwikkeling of het zelf overnemen.',
    deliverables: ['Acceptatieperiode', 'Optioneel onderhoudscontract', 'Optionele doorontwikkeling'],
  },
];

const Werkwijze: React.FC = () => {
  const navigate = useNavigate();
  usePageTitle('Werkwijze');

  return (
    <div className="min-h-screen">
      <div
        className="relative py-20"
        style={{
          backgroundImage: `url('/hero-background.webp')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="container mx-auto px-8">
          <button
            onClick={() => navigate('/')}
            className="mb-6 text-white hover:text-orange-400 flex items-center transition"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Terug naar Home
          </button>
          <h1 className="text-5xl font-bold text-white mb-4">Werkwijze</h1>
          <p className="text-xl text-white/90 max-w-3xl">
            Van eerste idee tot live website of app, in zeven heldere stappen.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-8 py-16">
        <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-lg p-8">
          <section className="mb-10">
            <p className="text-gray-700 leading-relaxed">
              Een goed project begint met een duidelijk proces. Bij SnapLogic werken we volgens een vaste
              aanpak, zodat je op elk moment weet waar we staan, wat je kunt verwachten en wat we van jou
              nodig hebben. Hieronder de complete route, van de eerste klik in de configurator tot na de
              lancering.
            </p>
          </section>

          <ol className="space-y-6">
            {steps.map((step, index) => (
              <li
                key={step.title}
                className="relative flex gap-5 p-5 rounded-xl border border-gray-100 hover:border-orange-200 hover:bg-orange-50/30 transition"
              >
                <div className="flex-shrink-0 flex flex-col items-center">
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold shadow-md"
                    style={{
                      background:
                        'radial-gradient(circle at center, #ff8a3d 0%, #f06c28 45%, #d4551b 100%)',
                    }}
                  >
                    {index + 1}
                  </div>
                  {index < steps.length - 1 && (
                    <div className="w-px flex-1 bg-gradient-to-b from-orange-300 to-transparent mt-2" />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4 mb-2 flex-wrap">
                    <h2 className="text-xl font-bold text-blue-900 flex items-center gap-2">
                      <span className="text-2xl">{step.icon}</span>
                      {step.title}
                    </h2>
                    <span className="text-xs font-semibold text-orange-700 bg-orange-100 px-3 py-1 rounded-full whitespace-nowrap">
                      {step.duration}
                    </span>
                  </div>
                  <p className="text-gray-700 mb-3 leading-relaxed">{step.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {step.deliverables.map((d) => (
                      <span
                        key={d}
                        className="text-xs text-gray-600 bg-gray-100 px-2.5 py-1 rounded-md"
                      >
                        {d}
                      </span>
                    ))}
                  </div>
                </div>
              </li>
            ))}
          </ol>

          <section className="mt-12 bg-blue-50 p-8 rounded-xl text-center">
            <h2 className="text-2xl font-bold text-blue-900 mb-3">Klaar om te starten?</h2>
            <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
              Stel je pakket samen in de configurator en ontvang direct een richtprijs. Daarna nemen we
              contact op voor de intake, meestal binnen één werkdag.
            </p>
            <button
              onClick={() => navigate('/?step=1')}
              className="text-white px-8 py-3 rounded-lg font-bold transition transform hover:scale-105 shadow-lg"
              style={{
                background:
                  'radial-gradient(circle at center, #ff8a3d 0%, #f06c28 45%, #d4551b 100%)',
              }}
            >
              Start je aanvraag
            </button>
          </section>
        </div>
      </div>
    </div>
  );
};

export default Werkwijze;
